'use client';

import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AuthApi } from '@/api';
import { AuthMutation, AuthQueryKey, UnauthQueryKey } from '@/api';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useLoginGuard } from '@/hooks/useLoginGuard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { closeResponsiveDialog, openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { getNowInKorea } from '@/utils/checkTodayInRange';
import { AffiliateGuideSheet, type AffiliateGuideSheetProps } from '../_components/AffiliateGuideSheet';
import { type AffiliateChannelType, type AffiliateOpenMode, type AppGuideModalPolicy, isAffiliateHotDealGone, resolveAffiliateRedirectHref, resolveAppOnlyMerchantAction, shouldNoticeAffiliateVerification, shouldOpenAffiliateGuide } from '../_utils/resolveAffiliateClick';
const VERIFICATION_NOTICE_STORAGE_KEY = 'affiliate-verification-noticed-date';
const readVerificationNoticedDate = () => {
  try {
    return window.localStorage.getItem(VERIFICATION_NOTICE_STORAGE_KEY);
  } catch {
    return null;
  }
};
const writeVerificationNoticedDate = (date: string) => {
  try {
    window.localStorage.setItem(VERIFICATION_NOTICE_STORAGE_KEY, date);
  } catch {
    return;
  }
};
import { resolveVisitLuckyTicketGrantMessage } from '../_utils/resolveVisitLuckyTicket';
export type AffiliateEntryPointType = NonNullable<Parameters<(typeof AuthApi)['post:/wello/v1/auth/affiliate/clicks']>[0]>['body']['entryPointType'];
type AffiliateClickTarget = {
  type: 'merchant';
} | {
  type: 'hotDeal';
  affiliateHotDealId: string;
} | {
  type: 'giftProductLink';
  affiliateGiftProductLinkId: string;
};
interface AffiliateClickRequest {
  target: AffiliateClickTarget;
  entryPointType: AffiliateEntryPointType;
  appGuideModalPolicy: AppGuideModalPolicy | undefined;
  openMode?: AffiliateOpenMode;
  channelType?: AffiliateChannelType;
  guide: Omit<AffiliateGuideSheetProps, 'onConfirm' | 'onClose'>;
}
interface AffiliateClickResponse {
  context?: {
    redirectUrl?: string;
    visitLuckyTicket?: {
      grantedCount?: number;
      identityVerificationRequiredYN?: boolean;
    } | null;
  };
}
export const useAffiliateClick = () => {
  const $ = _c(39);
  const loginGuard = useLoginGuard();
  const osType = useDeviceInfoStore(_temp);
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const {
    isWelloApp
  } = useCheckWelloApp();
  const setIsShowAppInstallConfirmModal = useGlobalStore(_temp2);
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = AuthMutation["post:/wello/v1/auth/affiliate/clicks"]();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const merchantClick = useMutation(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = AuthMutation["post:/wello/v1/auth/affiliate/clicks/hot-deals"]();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hotDealClick = useMutation(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = AuthMutation["post:/wello/v1/auth/affiliate/clicks/gift-product-links"]();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const giftProductLinkClick = useMutation(t2);
  const isPending = merchantClick.isPending || hotDealClick.isPending || giftProductLinkClick.isPending;
  let t3;
  if ($[3] !== toast) {
    t3 = () => {
      toast({
        message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694",
        type: "fail"
      });
    };
    $[3] = toast;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleIssueFailure = t3;
  let t4;
  if ($[5] !== handleIssueFailure || $[6] !== queryClient || $[7] !== toast) {
    t4 = error => {
      if (!isAffiliateHotDealGone(error)) {
        handleIssueFailure();
        return;
      }
      toast({
        message: "\uC9C0\uAE08\uC740 \uBCFC \uC218 \uC5C6\uB294 \uC0C1\uD488\uC774\uC5D0\uC694",
        type: "fail"
      });
      queryClient.invalidateQueries({
        queryKey: UnauthQueryKey["get:/wello/v1/allows/affiliate/hot-deals"]()
      });
      queryClient.invalidateQueries({
        queryKey: UnauthQueryKey["get:/wello/v1/allows/affiliate/products"]()
      });
      queryClient.invalidateQueries({
        queryKey: UnauthQueryKey["get:/wello/v1/allows/affiliate/products/{merchantCode}"]()
      });
    };
    $[5] = handleIssueFailure;
    $[6] = queryClient;
    $[7] = toast;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleHotDealIssueFailure = t4;
  let t5;
  if ($[9] !== queryClient || $[10] !== toast) {
    t5 = response => {
      queryClient.invalidateQueries({
        queryKey: AuthQueryKey["get:/wello/v1/auth/affiliate/merchants/lucky-ticket"]()
      });
      const visitLuckyTicket = response?.context?.visitLuckyTicket;
      if (!visitLuckyTicket) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: AuthQueryKey["get:/wello/v2/auth/benefit-tickets"]()
      });
      const grantMessage = resolveVisitLuckyTicketGrantMessage(visitLuckyTicket);
      if (grantMessage) {
        toast({
          message: grantMessage,
          type: "success"
        });
      }
    };
    $[9] = queryClient;
    $[10] = toast;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const syncVisitLuckyTicket = t5;
  let t6;
  if ($[12] !== isWelloApp || $[13] !== toast) {
    t6 = (entryPointType, response_0) => {
      const today = getNowInKorea().format("YYYY-MM-DD");
      if (!shouldNoticeAffiliateVerification({
        entryPointType,
        isWelloApp,
        isIdentityVerificationRequired: response_0?.context?.visitLuckyTicket?.identityVerificationRequiredYN === true,
        lastNoticedDate: readVerificationNoticedDate(),
        today
      })) {
        return;
      }
      writeVerificationNoticedDate(today);
      toast({
        message: "\uBCF8\uC778\uC778\uC99D \uD6C4 \uBC1B\uC744 \uC218 \uC788\uC5B4\uC694",
        type: "info"
      });
    };
    $[12] = isWelloApp;
    $[13] = toast;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const noticeVerificationIfNeeded = t6;
  let t7;
  if ($[15] !== handleIssueFailure || $[16] !== isWelloApp) {
    t7 = (openMode, response_1) => {
      const redirectUrl = response_1?.context?.redirectUrl;
      if (!redirectUrl) {
        handleIssueFailure();
        return;
      }
      window.location.href = resolveAffiliateRedirectHref({
        redirectUrl,
        openMode,
        isWelloApp
      });
    };
    $[15] = handleIssueFailure;
    $[16] = isWelloApp;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const moveToMerchant = t7;
  let t8;
  if ($[18] !== giftProductLinkClick || $[19] !== handleHotDealIssueFailure || $[20] !== handleIssueFailure || $[21] !== hotDealClick || $[22] !== loginGuard || $[23] !== merchantClick || $[24] !== moveToMerchant || $[25] !== noticeVerificationIfNeeded || $[26] !== syncVisitLuckyTicket) {
    t8 = t9 => {
      const {
        target,
        entryPointType: entryPointType_0,
        openMode: openMode_0,
        guide
      } = t9;
      if (!loginGuard()) {
        return;
      }
      if (target.type === "merchant") {
        merchantClick.mutate({
          body: {
            merchantCode: guide.merchantCode,
            entryPointType: entryPointType_0
          }
        }, {
          onSuccess: response_2 => {
            syncVisitLuckyTicket(response_2);
            noticeVerificationIfNeeded(entryPointType_0, response_2);
            moveToMerchant(openMode_0, response_2);
          },
          onError: handleIssueFailure
        });
        return;
      }
      if (target.type === "giftProductLink") {
        giftProductLinkClick.mutate({
          body: {
            affiliateGiftProductLinkId: target.affiliateGiftProductLinkId,
            entryPointType: entryPointType_0
          }
        }, {
          onSuccess: response_3 => moveToMerchant(openMode_0, response_3),
          onError: handleIssueFailure
        });
        return;
      }
      hotDealClick.mutate({
        body: {
          affiliateHotDealId: target.affiliateHotDealId,
          entryPointType: entryPointType_0
        }
      }, {
        onSuccess: response_4 => moveToMerchant(openMode_0, response_4),
        onError: handleHotDealIssueFailure
      });
    };
    $[18] = giftProductLinkClick;
    $[19] = handleHotDealIssueFailure;
    $[20] = handleIssueFailure;
    $[21] = hotDealClick;
    $[22] = loginGuard;
    $[23] = merchantClick;
    $[24] = moveToMerchant;
    $[25] = noticeVerificationIfNeeded;
    $[26] = syncVisitLuckyTicket;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const issueClick = t8;
  let t9;
  if ($[28] !== isDesktop || $[29] !== isPending || $[30] !== isWelloApp || $[31] !== issueClick || $[32] !== osType || $[33] !== setIsShowAppInstallConfirmModal || $[34] !== toast) {
    t9 = request => {
      if (isPending) {
        return;
      }
      const appOnlyAction = resolveAppOnlyMerchantAction({
        channelType: request.channelType,
        isWelloApp,
        osType
      });
      if (appOnlyAction === "INSTALL") {
        setIsShowAppInstallConfirmModal(true);
        return;
      }
      if (appOnlyAction === "APP_ONLY_NOTICE") {
        toast({
          message: "\uC571\uC5D0\uC11C\uB9CC \uC801\uB9BD\uB3FC\uC694",
          type: "info"
        });
        return;
      }
      if (!shouldOpenAffiliateGuide({
        policy: request.appGuideModalPolicy,
        osType,
        isDesktop
      })) {
        issueClick(request);
        return;
      }
      openResponsiveDialog({
        component: AffiliateGuideSheet,
        ariaLabel: `${request.guide.merchantName} 이동 안내`,
        drawerType: "bottom",
        props: {
          ...request.guide,
          onConfirm: () => {
            closeResponsiveDialog();
            issueClick(request);
          }
        }
      });
    };
    $[28] = isDesktop;
    $[29] = isPending;
    $[30] = isWelloApp;
    $[31] = issueClick;
    $[32] = osType;
    $[33] = setIsShowAppInstallConfirmModal;
    $[34] = toast;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const startAffiliateClick = t9;
  let t10;
  if ($[36] !== isPending || $[37] !== startAffiliateClick) {
    t10 = {
      startAffiliateClick,
      isPending
    };
    $[36] = isPending;
    $[37] = startAffiliateClick;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
};
function _temp(state) {
  return state.osType;
}
function _temp2(state_0) {
  return state_0.setIsShowAppInstallConfirmModal;
}